import type { Address, Cart, DeliveryGroup } from '@/modules/cart/domain/Cart';
import type {
  TBillingDeliveries,
  THomeDelivery,
  TIntangibleDelivery,
  TNoviosDelivery,
  TPickupDelivery
} from '@/modules/perseo-billing/interfaces/src/bff-web/billing';
import { useCustomerContext } from '@/sections/customer/CustomerContext';
import { useFindLocations } from '@/sections/region/hooks/useFindLocations';

// return YYYY-MM-DD format
export const formatDate = (date: Date): string => {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Santiago',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).format(date);
};

const transformHomeDelivery = (
  delivery: DeliveryGroup,
  shippingAddress: Address,
  findFunctions: ReturnType<typeof useFindLocations>
) => {
  const optionSelected = delivery?.optionSelected;
  const shippingOption = delivery?.shippingOption;

  const regionId = shippingAddress?.region ?? 0;
  const communeCode = shippingAddress?.commune ?? '';

  const { findRegionName, findCommuneName } = findFunctions;

  return {
    addressAlias: shippingAddress?.alias ?? '',
    recipientFullName: shippingAddress?.firstName ?? '',
    streetName: shippingAddress?.streetName ?? '',
    streetNumber: shippingAddress?.streetNumber ?? '',
    streetComplement: shippingAddress?.additionalAddressInfo ?? '',
    region: findRegionName(regionId),
    commune: findCommuneName(regionId, communeCode),

    shippingMethodType: shippingOption.shippingMethod ?? '',
    shippingServiceName: shippingOption.shippingMethod ?? '',

    deliveryDateFrom: formatDate(new Date(optionSelected.deliveryDateFrom)),
    deliveryDateTo: formatDate(new Date(optionSelected.deliveryDateTo)),
    shipDate: optionSelected.shipDate ?? '',
    timeSlot: optionSelected.timeSlot ?? {
      name: 'TH',
      deliveryTimeStart: '',
      deliveryTimeEnd: ''
    }
  };
};

enum SHIPPING_CAPABILITY {
  PICKUP_COPEC = 'PICKUP_COPEC'
}

const transformPickupDelivery = (
  delivery: DeliveryGroup,
  shippingAddress: Address
) => {
  const optionSelected = delivery?.optionSelected;
  const shippingOption = delivery?.shippingOption;

  const collectInfo = optionSelected.collectNodeAddress;
  const isPickupCopec =
    optionSelected?.shippingCapability === SHIPPING_CAPABILITY.PICKUP_COPEC;
  const optionDate = isPickupCopec
    ? optionSelected.deliveryDateFrom
    : optionSelected.shipDate;

  const deliveryDate = new Date(optionDate);
  const deliveryDateFormatted = formatDate(deliveryDate) ?? '';

  return {
    deliveryDateString: deliveryDateFormatted,
    pickupStoreCommune: collectInfo?.collectComunaName ?? '',
    pickupStoreName: optionSelected.collectNodeName ?? '',
    pickupStoreStreetName: collectInfo?.collectNodeAddress1 ?? '',
    pickupStoreStreetNumber: collectInfo?.collectNodeAddress2 ?? '',
    storeId: optionSelected.collectNodeCode ?? '',
    shippingMethodType: shippingOption.shippingMethod ?? '',
    shippingServiceName: shippingOption.shippingMethod ?? '',
    recipientFullName: shippingAddress?.firstName ?? '',
    proxyPickup: false
  };
};

export const useTransformCartToDeliveries = (
  cart?: Cart
): TBillingDeliveries => {
  const customerContext = useCustomerContext();
  const email = customerContext.customer.data?.email ?? '';
  const shippingAddress = cart?.shippingAddress;

  const findFunctions = useFindLocations();

  if (!cart || !shippingAddress) return {} as TBillingDeliveries;
  const deliveryGroups = cart?.deliveryInfo?.deliveryGroups;
  const homeDeliveries = deliveryGroups?.filter(
    (delivery) => delivery.shippingOption.shippingType === 'DELIVERY'
  );
  const pickupDeliveries = deliveryGroups?.filter(
    (delivery) => delivery.shippingOption.shippingType === 'COLLECT'
  );

  const outputHomeDeliveries: THomeDelivery[] =
    homeDeliveries?.map((delivery) => {
      const newHomeDelivery = transformHomeDelivery(
        delivery,
        shippingAddress,
        findFunctions
      );
      return newHomeDelivery;
    }) ?? [];

  const pickUps: TPickupDelivery[] =
    pickupDeliveries?.map((delivery) => {
      const newPickup = transformPickupDelivery(delivery, shippingAddress);
      return newPickup;
    }) ?? [];

  const intangibles = cart.lineItems.filter(
    (lineItem) => lineItem?.productType?.key === 'intangibles'
  );

  const intangiblesToReturn: TIntangibleDelivery[] = intangibles.map(() => {
    return {
      shippingMethodType: 'INTANGIBLE',
      customerEmail: email
    };
  });

  const novios: TNoviosDelivery[] = [];

  return {
    homeDelivery: outputHomeDeliveries,
    intangible: intangiblesToReturn,
    novios: novios,
    pickUp: pickUps
  };
};
